// skipcq: JS-W1028, JS-0128
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
// Material UI
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { parchmentStack, sidebarDrawerContentSx } from '../../utils/vintageSurfaces';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  LocalBar as LocalBarIcon,
  SportsBar as SportsBarIcon,
  CheckCircle as CheckCircleIcon,
  RateReview as RateReviewIcon,
  EmojiEvents as EmojiEventsIcon,
  BarChart as BarChartIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  AssignmentInd as AssignmentIndIcon,
  Email as EmailIcon,
  GroupWork as GroupWorkIcon,
  AccountTree as AccountTreeIcon,
  Storage as StorageIcon,
  Tune as TuneIcon,
  FactCheck as FactCheckIcon,
  Fingerprint as FingerprintIcon,
  LabelOutlined as DescriptorRequestsIcon,
  PendingActions as PendingActionsIcon,
  Campaign as CampaignIcon,
  Science as ScienceIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
// Context and utils
import { UserContext } from '../../context/UserContext';
import { auth } from '../../utils/firebase';

const drawerWidth = 240;

function LoadingScreen() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        ...parchmentStack(theme.palette.background.default),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress color="primary" />
    </Box>
  );
}

/** Match list routes plus related detail URLs (e.g. /brewery/:id). */
function pathnameMatchesNavItem(pathname, item) {
  if (typeof pathname !== 'string') {
    return false;
  }
  const matchers = item.pathMatchers ?? [item.path];
  return matchers.some(
    (prefix) =>
      typeof prefix === 'string' &&
      (pathname === prefix || pathname.startsWith(`${prefix}/`)),
  );
}

function navigationSectionMatchesPathname(section, pathname) {
  return section.items.some((item) =>
    pathnameMatchesNavItem(pathname, item),
  );
}

const dashboardNavItem = {
  text: 'Dashboard',
  icon: <DashboardIcon />,
  path: '/dashboard',
};

const settingsNavItem = {
  text: 'Settings',
  icon: <SettingsIcon />,
  path: '/settings',
};

const navigationSections = [
  {
    id: 'userActivity',
    label: 'User Activity',
    icon: <GroupWorkIcon />,
    items: [
      { text: 'Logged activity', icon: <CheckCircleIcon />, path: '/checkins' },
      { text: 'Reviews', icon: <RateReviewIcon />, path: '/reviews' },
    ],
  },
  {
    id: 'requests',
    label: 'Requests',
    icon: <PendingActionsIcon />,
    items: [
      {
        text: 'Claimed Breweries',
        icon: <AssignmentIndIcon />,
        path: '/claimed-breweries',
      },
      {
        text: 'Tag Requests',
        icon: <DescriptorRequestsIcon />,
        path: '/descriptor-requests',
      },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    icon: <CampaignIcon />,
    items: [
      {
        text: 'Newsletter Campaigns',
        icon: <EmailIcon />,
        path: '/newsletter-campaigns',
      },
      {
        text: 'Email Notifications',
        icon: <EmailIcon />,
        path: '/email-notifications',
      },
    ],
  },
  {
    id: 'dataManagement',
    label: 'Data Management',
    icon: <StorageIcon />,
    items: [
      {
        text: 'Breweries',
        icon: <BusinessIcon />,
        path: '/breweries',
        pathMatchers: ['/breweries', '/brewery'],
      },
      {
        text: 'Brewery groups',
        icon: <AccountTreeIcon />,
        path: '/brewery-groups',
      },
      { text: 'Beer', icon: <SportsBarIcon />, path: '/beer' },
      { text: 'Beer Styles', icon: <LocalBarIcon />, path: '/beer-styles' },
      {
        text: 'Crawler Scheduler',
        icon: <ScheduleIcon />,
        path: '/crawler-scheduler',
      },
      {
        text: 'Beer ingestion',
        icon: <FactCheckIcon />,
        path: '/beer-ingestion',
      },
      {
        text: 'Data Issues',
        icon: <WarningIcon />,
        path: '/failures',
      },
      { text: 'Events', icon: <EventIcon />, path: '/events' },
      {
        text: 'Users',
        icon: <PeopleIcon />,
        path: '/users',
        pathMatchers: ['/users', '/user'],
      },
    ],
  },
  {
    id: 'experiments',
    label: 'Experiments',
    icon: <ScienceIcon />,
    items: [
      {
        text: 'NH style fingerprints',
        icon: <FingerprintIcon />,
        path: '/nh-style-fingerprints',
      },
    ],
  },
  {
    id: 'utilities',
    label: 'Utilities',
    icon: <TuneIcon />,
    items: [
      { text: 'Metrics', icon: <BarChartIcon />, path: '/metrics' },
      { text: 'Splats', icon: <EmojiEventsIcon />, path: '/splats' },
    ],
  },
];

function NavigationSectionHeader({ section, isOpen, onToggleSection }) {
  const handleClick = useCallback(() => {
    onToggleSection(section.id);
  }, [section.id, onToggleSection]);

  return (
    <ListItem disablePadding>
      <ListItemButton onClick={handleClick}>
        <ListItemIcon>{section.icon}</ListItemIcon>
        <ListItemText primary={section.label} />
        {isOpen ? <ExpandLess /> : <ExpandMore />}
      </ListItemButton>
    </ListItem>
  );
}

function NavigationList() {
  const location = useLocation();
  const pathname = location.pathname;

  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    navigationSections.forEach((section) => {
      initial[section.id] = navigationSectionMatchesPathname(section, pathname);
    });
    return initial;
  });

  useEffect(() => {
    setOpenSections((previous) => {
      const next = { ...previous };
      let changed = false;
      navigationSections.forEach((section) => {
        const shouldOpen = navigationSectionMatchesPathname(section, pathname);
        if (shouldOpen && !next[section.id]) {
          next[section.id] = true;
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, [pathname]);

  const handleSectionToggle = useCallback((sectionId) => {
    setOpenSections((previous) => ({
      ...previous,
      [sectionId]: !previous[sectionId],
    }));
  }, []);

  function renderTopLevelNavItem(item) {
    const isSelected = pathnameMatchesNavItem(pathname, item);
    return (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          component={Link}
          to={item.path}
          selected={isSelected}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    );
  }

  function renderNestedNavItem(item) {
    const isSelected = pathnameMatchesNavItem(pathname, item);
    return (
      <ListItem key={item.text} disablePadding>
        <ListItemButton
          component={Link}
          to={item.path}
          selected={isSelected}
          sx={{ pl: 4 }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} />
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <List>
      {renderTopLevelNavItem(dashboardNavItem)}
      {navigationSections.map((section) => {
        const isOpen = Boolean(openSections[section.id]);
        return (
          <React.Fragment key={section.id}>
            <NavigationSectionHeader
              section={section}
              isOpen={isOpen}
              onToggleSection={handleSectionToggle}
            />
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {section.items.map(renderNestedNavItem)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      })}
      {renderTopLevelNavItem(settingsNavItem)}
    </List>
  );
}

function SignOutList({ handleSignOut }) {
  return (
    <List>
      <ListItem disablePadding>
        <ListItemButton onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </ListItemButton>
      </ListItem>
    </List>
  );
}

function DrawerContent({ handleSignOut }) {
  return (
    <Box sx={sidebarDrawerContentSx()}>
      <Toolbar variant="dense" sx={{ minHeight: 48 }}>
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            fontFamily: '"Henny Penny", cursive',
            fontWeight: 400,
            color: '#F9DA85',
          }}
        >
          I&apos;d Tap That
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.14)' }} />
      <NavigationList />
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.14)' }} />
      <SignOutList handleSignOut={handleSignOut} />
    </Box>
  );
}

function DashboardLayout({ title, children }) {
  const theme = useTheme();
  const user = useContext(UserContext);
  const [, loading] = useAuthState(auth);
  const navigate = useNavigate();

  // Mobile drawer state
  const [mobileOpen, setMobileOpen] = useState(false);

  // Handle navigation for unauthorized users as a side effect
  useEffect(() => {
    // Only proceed if loading is complete and user object is fully defined
    if (
      !loading &&
      user &&
      typeof user.isLoggedIn === 'boolean' &&
      typeof user.isGlobalAdmin === 'boolean'
    ) {
      // Navigate away if user is not logged in or not a global admin
      if (!user.isLoggedIn || !user.isGlobalAdmin) {
        navigate('/');
      }
    }
  }, [user, loading, navigate]);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  const handleSignOut = useCallback(() => {
    auth.signOut();
    navigate('/');
  }, [navigate]);

  const awaitingAuthOrUser =
    loading ||
    !user ||
    typeof user.isLoggedIn !== 'boolean' ||
    typeof user.isGlobalAdmin !== 'boolean';

  if (awaitingAuthOrUser) {
    return <LoadingScreen />;
  }

  // Return early if user is not authorized (loading is complete and user is confirmed not authorized)
  if (!user.isLoggedIn || !user.isGlobalAdmin) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2">Welcome, {user.userName}</Typography>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          <DrawerContent handleSignOut={handleSignOut} />
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          <DrawerContent handleSignOut={handleSignOut} />
        </Drawer>
      </Box>

      {/* Main content: parchment must live here so it paints behind the route (body alone may not show through #root / flex). */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          ...parchmentStack(theme.palette.background.default),
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: 48 }} />
        {children}
      </Box>
    </Box>
  );
}

export default DashboardLayout;
